import React, { useMemo, useState } from 'react';
import { Grid, GridCellProps, GridColumn, GridItemChangeEvent } from "@progress/kendo-react-grid";
import { DropDownList, DropDownListChangeEvent, DropDownListFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { Gateways, GatewayStatus } from '../../../../Models/Assets/AssetTemplateReadDto';
import { enMessages } from '../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import GatewaysReadDto from '../../../../Models/Assets/GatewaysReadDto';

interface Props {
  gateways: Gateways[];
  selectedGateway: { id: string; serialNumber: string };
  onGatewayChange: (gateway: { id: string; serialNumber: string }) => void;
  onGatewaysChange: (gateways: Gateways[]) => void;
  gatewaysQuery: { data?: GatewaysReadDto[] };
}

/** Unique key per row: use id when set, else temp key with index to avoid collisions */
function getDataItemKey(item: Gateways, index?: number): string {
  if (item.id) return item.id;
  return index !== undefined ? `temp-gw-${index}` : `temp-gw-${item.gatewayId}`;
}

const statusOptions: { label: string; value: GatewayStatus }[] = [
  { label: 'Enabled', value: 'Active' },
  { label: 'Disabled', value: 'Inactive' }
];

function gatewayStatus(gateway: Gateways): GatewayStatus {
  const status = gateway.status;
  return status ?? 'Inactive';
}

const GatewayMappingTableComponent: React.FC<Props> = ({
  gateways,
  onGatewayChange,
  onGatewaysChange,
  gatewaysQuery
}) => {
  const localizationService = useLocalization();

  const editableGateways = useMemo(() => {
    return gateways.map((gw, index) => ({
      ...gw,
      inEdit: true,
      _gridKey: getDataItemKey(gw, index)
    }));
  }, [gateways]);

  const handleItemChange = (event: GridItemChangeEvent) => {
    const key = (event.dataItem as any)._gridKey;
    const newGateways = gateways.map((gw: Gateways, idx: number) =>
      getDataItemKey(gw, idx) === key ? { ...gw, [event.field!]: event.value } : gw
    );
    onGatewaysChange(newGateways);
  };

  const availableGateways = gatewaysQuery?.data || [];
  const notConfigured = localizationService.toLanguageString('custom.notConfigured', enMessages.custom.notConfigured);
  const gatewayDropdownData = [
    { id: '', serialNumber: notConfigured },
    ...availableGateways
  ];

  /** Display gateway name: from gateway or lookup from gatewaysQuery (API) by id. Do not use serialNumber. */
  const nameCell = (props: GridCellProps) => {
    const gw = props.dataItem as Gateways;
    const apiGateway = gw.id ? availableGateways.find((g: GatewaysReadDto) => g.id === gw.id) : undefined;
    const displayName = gw.name || apiGateway?.name || '';
    return <td>{displayName}</td>;
  };

  const SerialNumberCell: React.FC<GridCellProps> = (props) => {
    const { dataItem } = props;
    const [filterText, setFilterText] = useState('');
    const gw = dataItem as Gateways;
    const currentSerial = gw.serialNumber ?? '';
    // Ensure current value is always in dropdown (Kendo needs value to exist in data)
    const currentValue = { id: gw.id, serialNumber: currentSerial || notConfigured };
    const baseDropdownData = gatewayDropdownData.some(g => g.id === gw.id)
      ? gatewayDropdownData
      : (gw.id ? [currentValue, ...gatewayDropdownData] : gatewayDropdownData);
    const filteredDropdownData = filterText
      ? baseDropdownData.filter(g => g.serialNumber.toLowerCase().includes(filterText.toLowerCase()))
      : baseDropdownData;
    const valueForDropdown = baseDropdownData.find(g => g.id === gw.id) ?? gatewayDropdownData[0];
    const handleFilterChange = (e: DropDownListFilterChangeEvent) => {
      setFilterText(e.filter.value);
    };
    const handleSerialChange = (e: DropDownListChangeEvent) => {
      const selected = e.target.value as GatewaysReadDto;
      if (!selected) return;
      setFilterText('');
      const key = (dataItem as any)._gridKey;
      const updatedGateway = {
        ...gw,
        id: selected.id,
        serialNumber: selected.id ? selected.serialNumber : '',
        name: selected.name || gw.name
      };
      const newGateways = gateways.map((g: Gateways, idx: number) =>
        getDataItemKey(g, idx) === key ? updatedGateway : g
      );
      onGatewaysChange(newGateways);
      onGatewayChange({ id: selected.id, serialNumber: selected.id ? selected.serialNumber : '' });
    };
    return (
      <td>
        <DropDownList
          style={{ width: '100%' }}
          data={filteredDropdownData}
          textField="serialNumber"
          dataItemKey="id"
          value={valueForDropdown}
          onChange={handleSerialChange}
          filterable
          onFilterChange={handleFilterChange}
        />
      </td>
    );
  };

  const serialNumberCell = (props: GridCellProps) => <SerialNumberCell {...props} />;

  const statusCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const status = gatewayStatus(dataItem as Gateways);
    const handleDropDownChange = (e: DropDownListChangeEvent) => {
      handleItemChange({
        dataItem: dataItem,
        field: 'status',
        value: e.target.value.value
      } as GridItemChangeEvent);
    };
    return (
      <td>
        <DropDownList
          style={{ width: '100%' }}
          data={statusOptions}
          textField="label"
          dataItemKey="value"
          value={statusOptions.find(opt => opt.value === status) ?? statusOptions.find(opt => opt.value === 'Inactive')}
          onChange={handleDropDownChange}
        />
      </td>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between align-middle mb-2">
        <div className="text-xl font-bold">
          {localizationService.toLanguageString('custom.gateways', enMessages.custom.gateways)}
        </div>
      </div>
      <div className="flex-1 min-h-0 overscroll-contain">
        <Grid
          data={editableGateways}
          style={{ height: '100%' }}
          dataItemKey="_gridKey"
          onItemChange={handleItemChange}
          editable={true}
          editField="inEdit"
        >
          <GridColumn
            field="name"
            title={localizationService.toLanguageString('custom.name', enMessages.custom.name)}
            editable={false}
            cells={{ data: nameCell }}
          />
          <GridColumn
            field="serialNumber"
            title={localizationService.toLanguageString('custom.serialNumber', enMessages.custom.serialNumber)}
            cells={{
              data: serialNumberCell
            }}
            editable={false}
          />
          <GridColumn
            field="status"
            title={localizationService.toLanguageString('custom.status', enMessages.custom.status)}
            cells={{
              data: statusCell
            }}
          />
        </Grid>
      </div>
    </div>
  );
};

export default GatewayMappingTableComponent;
